import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNavState } from "@/navigation/useNavState";
import { detailHref, drillHref, originStep } from "@/navigation/trail";
import { searchByQuery, similarTo, totalsFor } from "@/data/search";
import { StarIcon } from "@/assets/icons/StarIcon";
import { getAsset } from "@/data/catalogue";
import type { Asset, ScoredAsset, Stone } from "@/data/types";
import { applyRouteMode } from "@/styles/theme";
import ringSketch from "@/assets/images/ring.png";
import img07135 from "@/assets/images/07135.jpg";
import imgDrawing from "@/assets/images/121207.jpg";
import { useSaved } from "@/data/useSaved";

const RESULT_IMAGES = [img07135, imgDrawing, ringSketch];
const getImage = (i: number) => RESULT_IMAGES[i % RESULT_IMAGES.length];
import { useFlyingImage } from "@/transitions/FlyingImage";
import "./Results.css";

function countBy(
  results: ScoredAsset[],
  pick: (a: Asset) => string,
): Map<string, number> {
  const m = new Map<string, number>();
  for (const { asset } of results) {
    const k = pick(asset);
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

type FilterState = {
  category: Record<string, boolean>;
  material: Record<string, boolean>;
  acquisition: Record<string, boolean>;
  densityHigh: boolean;
  densityMid: boolean;
  densityLow: boolean;
};

const CATEGORIES = ["Anello", "Collana", "Bracciale", "Orecchini", "Spilla"];
const MATERIALS = ["Platino", "Oro bianco", "Oro giallo", "Oro rosa"];
const ACQUISITIONS = ["Luce strutturata", "Fotogrammetria"];

function initFilters(
  byCategory: Map<string, number>,
  byMaterial: Map<string, number>,
): FilterState {
  return {
    category: Object.fromEntries(
      CATEGORIES.map((k) => [k, (byCategory.get(k) ?? 0) > 0]),
    ),
    material: Object.fromEntries(
      MATERIALS.map((k) => [k, (byMaterial.get(k) ?? 0) > 0]),
    ),
    acquisition: Object.fromEntries(ACQUISITIONS.map((k) => [k, true])),
    densityHigh: true,
    densityMid: true,
    densityLow: false,
  };
}

export default function Results() {
  const navigate = useNavigate();
  const state = useNavState();
  const [exiting, setExiting] = useState(false);
  const featuredImgRef = useRef<HTMLImageElement>(null);
  const featuredSecRef = useRef<HTMLElement>(null);
  const { trigger, setImageSrc } = useFlyingImage();

  useEffect(() => {
    applyRouteMode("light");
  }, []);

  const origin = originStep(state.trail);

  const results = useMemo<ScoredAsset[]>(
    () => (origin ? similarTo(origin.ref) : searchByQuery(state.query)),
    [origin, state.query],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [animPhase, setAnimPhase] = useState<"mount" | "out" | "in" | null>(
    "mount",
  );

  useEffect(() => {
    const t = setTimeout(() => setAnimPhase(null), 2100 + 1200);
    return () => clearTimeout(t);
  }, []);

  const selectIndex = useCallback(
    (i: number) => {
      if (i === selectedIndex || animPhase) return;
      setAnimPhase("out");
      setTimeout(() => {
        setSelectedIndex(i);
        setAnimPhase("in");
        setTimeout(() => setAnimPhase(null), 900);
      }, 900);
    },
    [selectedIndex, animPhase],
  );
  const featured = results[selectedIndex]?.asset;

  const openViewer = useCallback(() => {
    if (exiting || !featured) return;
    setExiting(true);
    setImageSrc(getImage(selectedIndex));
    const src = featuredImgRef.current?.getBoundingClientRect();
    const panel = featuredSecRef.current?.getBoundingClientRect();
    if (src && panel) trigger(src, panel);
    setTimeout(() => navigate(detailHref(featured.ref, state)), 2300);
  }, [exiting, featured, selectedIndex, navigate, trigger, setImageSrc, state]);
  const totals = totalsFor(results);
  const { toggle, isSaved } = useSaved();
  const byCategory = countBy(results, (a) => a.categoryLabel);
  const byMaterial = countBy(results, (a) => a.material);
  const byAcquisition = countBy(results, (a) => a.acquisition);
  const dense = results.filter((r) => r.asset.trianglesM >= 2).length;

  const [filters, setFilters] = useState<FilterState>(() =>
    initFilters(byCategory, byMaterial),
  );

  const setCheck = (group: keyof FilterState, key: string, val: boolean) =>
    setFilters((f) => ({
      ...f,
      [group]: { ...(f[group] as Record<string, boolean>), [key]: val },
    }));

  const resetFilters = () =>
    setFilters({
      category: Object.fromEntries(CATEGORIES.map((k) => [k, false])),
      material: Object.fromEntries(MATERIALS.map((k) => [k, false])),
      acquisition: Object.fromEntries(ACQUISITIONS.map((k) => [k, false])),
      densityHigh: false,
      densityMid: false,
      densityLow: false,
    });

  return (
    <div className={`results-clip${exiting ? " exiting" : ""}`}>
      <div className="body">
        <aside className="filters" aria-label="Filtri">
          <h2>Filtri</h2>

          {origin && (
            <div className="fg refbox">
              <h3>Riferimento visivo</h3>
              <div className="refcard">
                <span className="thumb">
                  <img src={getImage(selectedIndex)} alt="" />
                </span>
                <span className="tx">
                  <span className="nm">
                    {getAsset(origin.ref)?.title ?? origin.ref}
                  </span>
                  <span className="rf">{origin.ref}</span>
                </span>
              </div>
            </div>
          )}

          <div className="fg">
            <h3>Oggetto</h3>
            {CATEGORIES.map((k) => (
              <label key={k}>
                <input
                  type="checkbox"
                  checked={filters.category[k]}
                  onChange={(e) => setCheck("category", k, e.target.checked)}
                />{" "}
                {k} <span className="n">{byCategory.get(k) ?? 0}</span>
              </label>
            ))}
          </div>

          <div className="fg">
            <h3>Materiale</h3>
            {MATERIALS.map((k) => (
              <label key={k}>
                <input
                  type="checkbox"
                  checked={filters.material[k]}
                  onChange={(e) => setCheck("material", k, e.target.checked)}
                />{" "}
                {k} <span className="n">{byMaterial.get(k) ?? 0}</span>
              </label>
            ))}
          </div>

          <div className="fg">
            <h3>Acquisizione</h3>
            {ACQUISITIONS.map((k) => (
              <label key={k}>
                <input
                  type="checkbox"
                  checked={filters.acquisition[k]}
                  onChange={(e) => setCheck("acquisition", k, e.target.checked)}
                />{" "}
                {k} <span className="n">{byAcquisition.get(k) ?? 0}</span>
              </label>
            ))}
          </div>

          <div className="fg">
            <h3>Densità mesh</h3>
            <label>
              <input
                type="checkbox"
                checked={filters.densityHigh}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, densityHigh: e.target.checked }))
                }
              />{" "}
              ≥ 2.0 M tri <span className="n">{dense}</span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={filters.densityMid}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, densityMid: e.target.checked }))
                }
              />{" "}
              0.5 - 2.0 M tri{" "}
              <span className="n">{results.length - dense}</span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={filters.densityLow}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, densityLow: e.target.checked }))
                }
              />{" "}
              &lt; 0.5 M tri <span className="n">0</span>
            </label>
          </div>

          <button type="button" className="reset" onClick={resetFilters}>
            Azzera filtri
          </button>
        </aside>

        <div className="resultwrap">
          {featured ? (
            <>
              <div
                className={["featured-clip", animPhase]
                  .filter(Boolean)
                  .join(" ")}
              >
                <section
                  ref={featuredSecRef}
                  className="featured"
                  aria-label="Prima corrispondenza"
                >
                  <div className="fig">
                    <div className="frame">
                      <img
                        ref={featuredImgRef}
                        src={getImage(selectedIndex)}
                        alt={featured.label}
                      />
                    </div>
                  </div>
                  <div className="data">
                    <div className="fhead">
                      <h2>{featured.label}</h2>
                      <div className="adv">
                        <span className="m-label">Match</span>
                        <span className="bar">
                          <i
                            style={{
                              width: `${results[selectedIndex].match}%`,
                            }}
                          />
                        </span>
                        <span className="pct">
                          {results[selectedIndex].match}%
                        </span>
                      </div>
                    </div>

                    <div className="fieldgrid">
                      <Field label="REF" value={featured.ref} />
                      <Field label="Ubicazione" value={featured.location} />
                      <Field
                        label="Oggetto"
                        value={`${featured.categoryLabel} · ${featured.material}`}
                      />
                      <Field
                        label="Densità mesh"
                        value={`${featured.triangles} triangoli · watertight`}
                      />
                      <Field
                        label="Risoluzione texture"
                        value={featured.texture}
                      />
                      <Field
                        label="Metodo acquisizione"
                        value={featured.acquisitionDetail}
                      />
                      <Field label="Data acquisizione" value={featured.date} />
                      <Field label="Formati" value={featured.formats} />
                      <Field label="Dimensione file" value={featured.size} />
                    </div>

                    <div className="facts">
                      <div className="facts-left">
                        <button
                          className="primary"
                          type="button"
                          onClick={openViewer}
                        >
                          Apri viewer
                        </button>
                        <button
                          className="ghost ai"
                          type="button"
                          onClick={() =>
                            navigate(drillHref(featured.ref, state))
                          }
                        >
                          ✦ Trova simili
                        </button>
                      </div>
                      <button
                        className={`ghost star-btn${isSaved(featured.ref) ? " saved" : ""}`}
                        type="button"
                        onClick={() => toggle(featured.ref)}
                        aria-label={
                          isSaved(featured.ref)
                            ? "Rimuovi dai salvati"
                            : "Salva"
                        }
                      >
                        <StarIcon size={18} filled={isSaved(featured.ref)} />
                      </button>
                    </div>
                  </div>
                </section>
              </div>

              <div className="panel-bottom">
                <div className="tablescroll">
                  <table className="grid">
                    <thead>
                      <tr>
                        {[
                          "REF",
                          "Oggetto",
                          "Pietre",
                          "Materiale",
                          "Mesh",
                          "Texture",
                          "Acquisizione",
                          "Data",
                          "Dim.",
                          "Match",
                        ].map((h) => (
                          <th key={h}>
                            <ColHeader label={h} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {results.map(({ asset, match }, i) => (
                        <tr
                          key={asset.ref}
                          className={
                            i === selectedIndex ? "selected" : undefined
                          }
                          onClick={() => selectIndex(i)}
                        >
                          <td className="mono">{asset.ref}</td>
                          <td className="obj">
                            <button
                              type="button"
                              className="obj-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                selectIndex(i);
                              }}
                            >
                              {asset.label}
                            </button>
                          </td>
                          <td>
                            <StoneDots stones={asset.stones} />
                          </td>
                          <td>{asset.material}</td>
                          <td className="mono">{asset.triangles}</td>
                          <td className="mono">
                            {asset.texture.split(" ")[0]}
                            <sup>2</sup>
                          </td>
                          <td>
                            {asset.acquisition === "Luce strutturata"
                              ? "Luce strutt."
                              : "Fotogramm."}
                          </td>
                          <td className="mono">{asset.date}</td>
                          <td className="mono">{asset.size}</td>
                          <td>
                            <span className="matchcell">
                              <span className="bar">
                                <i
                                  className={match >= 80 ? "hi" : "mid"}
                                  style={{ width: `${match}%` }}
                                />
                              </span>
                              <span className="val">{match}</span>
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="totals">
                  <Total label="Risultati" value={String(totals.count)} />
                  <Total label="Anelli" value={String(totals.rings)} />
                  <Total label="Selezionati" value="1" />
                  <Total label="Match medio" value={`${totals.avgMatch}%`} />
                  <Total label="Dim. totale" value={`${totals.sizeGb} GB`} />
                </div>
              </div>
            </>
          ) : (
            <div className="noresults">
              <div className="tt">
                Nessun asset affine sopra la soglia di confidenza.
              </div>
              <div className="ds">
                Il pezzo di partenza è troppo distante dal resto dell'archivio.
                Allarga la ricerca o riparti dalla query originale.
              </div>
              <button type="button" className="pill">
                Torna ai risultati iniziali
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ColHeader({ label }: { label: string }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const toggle = () => {
    setOpen((v) => !v);
    if (!open) setTimeout(() => inputRef.current?.focus(), 0);
  };

  return open ? (
    <input
      ref={inputRef}
      className="col-filter"
      placeholder={label}
      onBlur={() => setOpen(false)}
      onClick={(e) => e.stopPropagation()}
    />
  ) : (
    <button type="button" className="col-label" onClick={toggle}>
      {label} <span className="filt">▾</span>
    </button>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="field-cell">
      <span className="m-label">{label}</span>
      <div className="fval">{value}</div>
    </div>
  );
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <div className="t">
      <span className="m-label">{label}</span>
      <span className="tv">{value}</span>
    </div>
  );
}

const STONE_COLORS: Array<{
  keywords: string[];
  color: string;
  label: string;
}> = [
  { keywords: ["diamant"], color: "#e8e8f0", label: "Diamante" },
  { keywords: ["ametist", "lavanda"], color: "#9b59b6", label: "Ametista" },
  { keywords: ["perla"], color: "#f5e6d3", label: "Perla" },
  { keywords: ["giada", "rosett"], color: "#27ae60", label: "Giada" },
  { keywords: ["smerald"], color: "#2ecc71", label: "Smeraldo" },
  { keywords: ["zaffir"], color: "#2980b9", label: "Zaffiro" },
  { keywords: ["rubino"], color: "#e74c3c", label: "Rubino" },
];

function stoneEntry(pos: string): { color: string; label: string } | null {
  const lower = pos.toLowerCase();
  for (const entry of STONE_COLORS) {
    if (entry.keywords.some((k) => lower.includes(k))) return entry;
  }
  return null;
}

function StoneDots({ stones }: { stones: Stone[] }) {
  const seen = new Map<string, { color: string; label: string }>();
  for (const s of stones) {
    const entry = stoneEntry(s.position);
    if (entry && !seen.has(entry.label)) seen.set(entry.label, entry);
  }
  if (seen.size === 0) return null;
  return (
    <span className="stone-dots">
      {[...seen.values()].map(({ color, label }) => (
        <span
          key={label}
          className="stone-dot"
          style={{ background: color }}
          title={label}
        />
      ))}
    </span>
  );
}
